/**
 * Checkbox — Base UI Checkbox primitive.
 *
 * Design system mapping (mch-check checkbox):
 *   • Box: 16x16, border webex-gray-500 / dark: webex-gray-400, rounded-[2px]
 *   • Hover border: webex-blue-600
 *   • Checked: bg + border webex-blue-600, white check icon
 *   • Indeterminate: bg + border webex-blue-600, white horizontal line
 *   • Focus: ring-2 webex-blue-600 ring-offset-2
 *   • Disabled: opacity-50
 *
 * Use inside <Field> for ARIA wiring + label coupling.
 */
import * as React from "react"
import { Checkbox as BaseCheckbox } from "@base-ui-components/react/checkbox"
import { cn } from "@lib/cn"

export interface CheckboxProps
  extends React.ComponentPropsWithoutRef<typeof BaseCheckbox.Root> {
  state?: "default" | "error"
}

export const Checkbox = React.forwardRef<HTMLButtonElement, CheckboxProps>(
  ({ className, state = "default", ...props }, ref) => {
    return (
      <BaseCheckbox.Root
        ref={ref as React.Ref<HTMLElement>}
        className={cn(
          "inline-flex size-4 shrink-0 items-center justify-center rounded-[2px]",
          "border bg-white dark:bg-webex-gray-950",
          "transition-colors duration-100 motion-reduce:transition-none cursor-pointer",
          "hover:border-webex-blue-600",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-webex-blue-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-webex-gray-950",
          "data-[checked]:bg-webex-blue-600 data-[checked]:border-webex-blue-600",
          "data-[indeterminate]:bg-webex-blue-600 data-[indeterminate]:border-webex-blue-600",
          "data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed",
          state === "default"
            ? "border-webex-gray-500 dark:border-webex-gray-400"
            : "border-webex-red-600 dark:border-webex-red-500",
          className,
        )}
        {...props}
      >
        <BaseCheckbox.Indicator
          className="flex size-full items-center justify-center text-white data-[unchecked]:hidden"
          render={(p, state) => (
            <span {...p}>
              {state.indeterminate ? (
                <svg viewBox="0 0 16 16" className="size-3" aria-hidden="true">
                  <path d="M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              ) : (
                <svg viewBox="0 0 16 16" className="size-3" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3.5,8.5 6.5,11.5 12.5,5" />
                </svg>
              )}
            </span>
          )}
        />
      </BaseCheckbox.Root>
    )
  },
)
Checkbox.displayName = "Checkbox"
